import React from 'react';

// Lazy load widgets for code splitting
const Campaigns = React.lazy(() => import('@/widgets/dooh/Campaigns'));
const ApproveScreen = React.lazy(() => import('@/widgets/dooh/ApproveScreen'));
const Screens = React.lazy(() => import('@/widgets/dooh/Screens'));
const ViewScreen = React.lazy(() => import('@/widgets/dooh/ViewScreen'));
const DoohOps = React.lazy(() => import('@/widgets/dooh/DoohOps'));
const Notification = React.lazy(() => import('@/widgets/Notification'));
const AdvertiserCampaign = React.lazy(
  () => import('@/widgets/dooh/AdvertiserCampaign')
);
const Content = React.lazy(() => import('@/widgets/dooh/Content'));
const ViewCampaign = React.lazy(() => import('@/widgets/dooh/ViewCampaign'));
const EditCampaign = React.lazy(() => import('@/widgets/dooh/EditCampaign'));
const EditCampaignFlow = React.lazy(
  () => import('@/widgets/dooh/EditCampaignFlow')
);
const Report = React.lazy(() => import('@/widgets/dooh/Report'));
const GenerateReport = React.lazy(
  () => import('@/widgets/dooh/GenerateReport')
);
const DownloadDetailedReport = React.lazy(
  () => import('@/widgets/dooh/DownloadDetailedReport')
);
const Home = React.lazy(() => import('@/widgets/dooh/Home'));
const EditScreen = React.lazy(() => import('@/widgets/dooh/EditScreen'));
const RetargetingCampaigns = React.lazy(
  () => import('@/widgets/dooh/RetargetingCampaigns')
);
const RetargetingCampaignNew = React.lazy(
  () => import('@/widgets/dooh/RetargetingCampaignNew')
);
const ViewRetargetingCampaign = React.lazy(
  () => import('@/widgets/dooh/ViewRetargetingCampaign')
);
const UserManagement = React.lazy(
  () => import('@/widgets/dooh/UserManagement')
);
const Approval = React.lazy(() => import('@/widgets/dooh/Approval'));

// Define widget configuration interface
export interface WidgetConfig {
  id: string;
  widget: string;
  props?: Record<string, any>;
  layout?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
}

// Widget registry with lazy-loaded components
const widgetRegistry: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<any>>
> = {
  Campaigns,
  ApproveScreen,
  Screens,
  ViewScreen,
  DoohOps,
  Notification,
  AdvertiserCampaign,
  Content,
  ViewCampaign,
  EditCampaign,
  EditCampaignFlow,
  Report,
  GenerateReport,
  DownloadDetailedReport,
  Home,
  EditScreen,
  RetargetingCampaigns,
  RetargetingCampaignNew,
  ViewRetargetingCampaign,
  UserManagement,
  Approval,
};

/**
 * Renders a widget by its ID with the provided props
 * @param id - Widget identifier
 * @param props - Props to pass to the widget
 * @returns React element or null if widget not found
 */
export const renderWidgetById = (
  id: string,
  props: Record<string, any> = {}
): React.ReactElement | null => {
  const Widget = widgetRegistry[id];

  if (!Widget) {
    return (
      <div
        style={{
          padding: '20px',
          border: '2px dashed #ccc',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#666',
        }}
      >
        <p>Widget "{id}" not found</p>
        <small>
          Available widgets: {Object.keys(widgetRegistry).join(', ')}
        </small>
      </div>
    );
  }

  return <Widget {...props} />;
};

/**
 * Renders a widget with error boundary
 * @param id - Widget identifier
 * @param props - Props to pass to the widget
 * @returns React element with error boundary
 */
export const renderWidgetWithErrorBoundary = (
  id: string,
  props: Record<string, any> = {}
): React.ReactElement => {
  return (
    <WidgetErrorBoundary widgetId={id}>
      {renderWidgetById(id, props)}
    </WidgetErrorBoundary>
  );
};

/**
 * Error boundary component for widgets
 */
class WidgetErrorBoundary extends React.Component<
  { widgetId: string; children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { widgetId: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Widget ${this.props.widgetId} crashed:`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: '20px',
            border: '2px solid #f44336',
            borderRadius: '8px',
            backgroundColor: '#ffebee',
            color: '#c62828',
          }}
        >
          <h4>Widget Error</h4>
          <p>Widget "{this.props.widgetId}" failed to load.</p>
          <button
            onClick={this.handleRetry}
            style={{
              marginTop: '8px',
              padding: '4px 12px',
              cursor: 'pointer',
              border: '1px solid #c62828',
              borderRadius: '4px',
              background: 'transparent',
              color: '#c62828',
            }}
          >
            Retry
          </button>
          <details style={{ marginTop: '10px' }}>
            <summary>Error details</summary>
            <pre style={{ fontSize: '12px', marginTop: '10px' }}>
              {this.state.error?.toString()}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}
